import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { computeCriticalPathResolved, projectPhasesForCriticalPath } from '../../../../lib/criticalPathAnalysis';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialDataTable, EditorialCallout } from '../../report/editorial';
import { resolveSectionCallout } from '../../../../lib/reportRendering/reportSectionCallouts';
import { SectionShell } from '../SectionShell';
import { resolveSupplyChainPlan } from '../../../../lib/reportRendering/supplyChainPlan';

/**
 * Slack cell — preserves the legacy formatting (`X.X mo` / `X.X mo overdue`
 * in the negative role ink). Non-finite slack (no COD on the intent → the
 * engine emits `Infinity` for user-flagged lines) renders an em-dash rather
 * than the literal "Infinity" the old list would have printed.
 */
function slackCell(theme: ReturnType<typeof useEffectiveReportTheme>, slackMonths: number | null | undefined): React.ReactNode {
  if (slackMonths == null || !Number.isFinite(slackMonths)) return '–';
  if (slackMonths < 0) {
    return (
      <span style={{ color: roleColor(theme, 'negative') }}>
        {Math.abs(slackMonths).toFixed(1)} mo overdue
      </span>
    );
  }
  return `${slackMonths.toFixed(1)} mo`;
}

/**
 * String-slack cell for the LLM `longLeadVsCod` table. Unlike the
 * deterministic register's numeric slackCell, the model emits a free-text
 * slack string (e.g. "-2 months", "on schedule"). Mirror slackCell's
 * negative-ink role: when the string's first non-space char is a minus the
 * item already breaches the COD → paint it in the negative role ink; else
 * render plain (citation-walked so any inline [Ref N] stays live).
 */
function llcSlackCell(
  theme: ReturnType<typeof useEffectiveReportTheme>,
  slack: string | undefined,
  refs: SiteRagReport['references'],
): React.ReactNode {
  const s = slack?.trim();
  if (!s) return '–';
  const walked = walkCitationText(s, refs);
  if (s.trimStart().startsWith('-')) {
    return <span style={{ color: roleColor(theme, 'negative') }}>{walked}</span>;
  }
  return walked;
}

/**
 * W2b editorial port — the hairline <ul> becomes an EditorialDataTable
 * (Item × Lead × Slack). NOT EditorialTimeline: the critical-path engine
 * emits lead-time durations plus a slack figure anchored to the RENDER-time
 * "today" (and `Infinity` when the intent has no COD) — there are no fixed
 * per-item date windows to plot, so a gantt would paint bars that shift by
 * render date / fabricate windows. The register keeps every legacy signal
 * (name, worst-case lead, slack with the overdue negative-ink emphasis).
 *
 * Inputs unchanged: D1 phase-aware resolution (per-phase CODs from the
 * gen-time intent snapshot), top-12 slice, intro prose with citation
 * walking, identical hasData gate.
 */
export const CriticalPathProcurementSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const plan = resolveSupplyChainPlan(report);
  // D1 — score each phase's BoM lines against ITS OWN COD when the project is
  // genuinely phased (else single-COD, unchanged). Phase CODs and the ultimate
  // COD both come from the gen-time intent snapshot so the section stays
  // reproducible alongside the rest of the frozen report.
  const phases = projectPhasesForCriticalPath(report.projectIntentAtGen);
  const cod = report.projectIntentAtGen?.inServiceDate ?? null;
  const cp = plan ? computeCriticalPathResolved(plan, cod, phases) : null;
  const items = cp?.criticalPathItems ?? [];
  const intro = report.supplyChainAnalysis?.criticalPathAssessment?.trim();
  // OPTIONAL LLM long-lead-equipment-vs-COD gate — appended below the
  // deterministic register. COD-gated on having at least one item row.
  const llc = report.supplyChainAnalysis?.longLeadVsCod;
  const llcItems = llc?.items?.filter((it) => !!it && (it.item || it.leadTimeMonths || it.orderByDate || it.slackVsCod || it.slipRisk)) ?? [];
  const hasLlc = llcItems.length > 0;
  const hasData = items.length > 0 || !!intro || hasLlc;

  const callout = resolveSectionCallout(report, 'criticalPathProcurement');

  return (
    <SectionShell label={label} hasData={hasData} report={report} sectionId="criticalPathProcurement" required>
      {intro && (
        <p className="text-sm mb-4" style={{ color: roleColor(theme, 'body') }}>{walkCitationText(intro, report.references)}</p>
      )}
      {callout && (
        <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
          {callout.body}
        </EditorialCallout>
      )}
      {items.length > 0 && (
        /* data-span-columns: defensive — lets the register span both flow
           columns if this section's supportsColumnLayout is flipped on.
           Carrier div holds the testid (EditorialDataTable has no testid prop). */
        <div data-span-columns className="overflow-x-auto" data-testid="critical-path-register">
          <EditorialDataTable
            theme={theme}
            columns={[
              { header: 'Item' },
              { header: 'Lead (mo)', numeric: true, width: 110 },
              { header: 'Slack', numeric: true, width: 150 },
            ]}
            rows={items.slice(0, 12).map((item) => [
              item.name,
              item.leadTimeMonthsHigh,
              slackCell(theme, item.slackMonths),
            ])}
          />
        </div>
      )}
      {hasLlc && (
        /* OPTIONAL LLM long-lead-equipment-vs-COD table — SUPPLEMENTS (does
           NOT replace) the deterministic register above. The register is
           BoM-derived ground truth; this LLM table adds the binding-equipment
           narrative (SMR vessel, turbine OEM slots, transformers) the BoM may
           not enumerate, gated against the COD. Labelled distinctly so it is
           not confused with the deterministic register. */
        <div data-span-columns className="overflow-x-auto" data-testid="long-lead-vs-cod">
          <p
            className="text-xs mb-2"
            style={{ color: roleColor(theme, 'muted'), fontFamily: 'var(--report-body-font, inherit)', textTransform: 'uppercase', letterSpacing: '0.12em' }}
          >
            Long-Lead Equipment vs COD{llc?.codDate ? ` – vs COD ${llc.codDate}` : ''}
          </p>
          <EditorialDataTable
            theme={theme}
            columns={[
              { header: 'Item' },
              { header: 'Lead (mo)', numeric: true, width: 90 },
              { header: 'Order-by', width: 110 },
              { header: 'Slack vs COD', width: 130 },
              { header: 'Slip Risk' },
            ]}
            rows={llcItems.map((it) => [
              walkCitationText(it.item ?? '–', report.references),
              walkCitationText(it.leadTimeMonths ?? '–', report.references),
              walkCitationText(it.orderByDate ?? '–', report.references),
              llcSlackCell(theme, it.slackVsCod, report.references),
              walkCitationText(it.slipRisk ?? '–', report.references),
            ])}
          />
          {llc?.bindingConstraint?.trim() && (
            <p
              className="text-sm mt-2"
              style={{ color: roleColor(theme, 'body'), fontWeight: 600 }}
              data-testid="long-lead-binding-constraint"
            >
              Binding constraint: {walkCitationText(llc.bindingConstraint, report.references)}
            </p>
          )}
        </div>
      )}
    </SectionShell>
  );
};
